import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VendorsList from './vendors_list';
import VendorManagement from './vendor_management';
import ApprovalMatrix from './approval_matrix';
import { useAuth } from "../../context/AuthContext";
import "../../styles.css";

// Fixed slots — 0=Vendors List, 1=Vendor Management, 2=Approval Matrix — kept
// stable regardless of which tabs are hidden, so the mobile ?tab= mapping and
// the desktop segmented control never disagree about what a given index means.
const VENDOR_TABS = [
  { index: 0, key: "vendors", label: "Vendors List", accessField: "vendorListTab" },
  { index: 1, key: "payments", label: "Vendor Management", accessField: "vendorManagementTab" },
  { index: 2, key: "approvals", label: "Approval Matrix", accessField: "approvalMatrixTab" },
];

const Vendor = () => {
  const { user } = useAuth();
  const isMainUser = user?.user_type === 'main';
  const accessOf = (field) => (isMainUser ? 'full_access' : (user?.access_rights?.[field] || 'no_access'));
  const visibleTabs = VENDOR_TABS.filter((t) => accessOf(t.accessField) !== 'no_access');

  const [tabValue, setTabValue] = useState(1); // Default to Vendor Management tab
  const location = useLocation();

  // On mobile the floating bottom pill drives the section via /vendor?tab=...
  useEffect(() => {
    const key = new URLSearchParams(location.search).get('tab');
    const map = { vendors: 0, payments: 1, approvals: 2 };
    if (key && map[key] !== undefined) setTabValue(map[key]);
  }, [location.search]);

  // If the current tab isn't accessible (e.g. it was just revoked, or the
  // default landed on a hidden tab), fall back to the first visible one.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.some((t) => t.index === tabValue)) {
      setTabValue(visibleTabs[0].index);
    }
    // eslint-disable-next-line
  }, [visibleTabs.map((t) => t.index).join(","), tabValue]);

  if (visibleTabs.length === 0) {
    return (
      <div className="client-container">
        <Box sx={{ textAlign: "center", py: 10, color: "#98a2b3" }}>
          <LockOutlinedIcon sx={{ fontSize: 34, mb: 1.5, color: "#d0d5dd" }} />
          <Typography sx={{ fontSize: "15px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", color: "#475467" }}>
            Access Denied
          </Typography>
          <Typography sx={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", mt: 0.5 }}>
            You don't have permission to view any vendor section.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="client-container">

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        {/* Desktop: premium segmented control (same tabValue) */}
        <Box
          sx={{
            display: "inline-flex",
            gap: "4px",
            background: "#f2f4f7",
            borderRadius: "13px",
            p: "4px",
          }}
        >
          {visibleTabs.map(({ index, label }) => {
            const active = tabValue === index;
            return (
              <Box
                key={label}
                onClick={() => setTabValue(index)}
                sx={{
                  px: 2.25,
                  py: "8px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: active ? 700 : 600,
                  fontFamily: "Roboto, Arial, sans-serif",
                  color: active ? "#1565C0" : "#667085",
                  background: active ? "#ffffff" : "transparent",
                  boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { color: "#1565C0" },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ marginTop: "20px" }}>
        {tabValue === 0 && accessOf("vendorListTab") !== 'no_access' && <VendorsList accessLevel={accessOf("vendorListTab")} />}
        {tabValue === 1 && accessOf("vendorManagementTab") !== 'no_access' && <VendorManagement accessLevel={accessOf("vendorManagementTab")} />}
        {tabValue === 2 && accessOf("approvalMatrixTab") !== 'no_access' && <ApprovalMatrix accessLevel={accessOf("approvalMatrixTab")} />}
      </Box>
    </div>
  );
};

export default Vendor;
